"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { inputClass, labelClass, primaryButtonClass } from "@/components/form";
import { Segmented } from "@/components/Segmented";
import { SIGNATURE_FONTS } from "@/app/sign-pdf/fonts";
import { trimmedPng, type Signature } from "@/components/sign/signature-canvas";
import { acceptAttribute, IMAGE_KINDS, kindOf } from "@/lib/files";
import { removeWhiteBackground } from "@/lib/sign/signature";
import { cn } from "@/lib/utils";
import { runImageJob } from "@/workers/image-client";
const INKS = [{
  value: "#0f1222",
  label: "Black"
}, {
  value: "#1c3f9e",
  label: "Blue"
}];
type Tab = "draw" | "type" | "upload";

/** Draw, type or upload a signature (FR-14). Calls onDone with a trimmed PNG. */
export function SignatureMaker(t0) {
  const $ = _c(24);
  const {
    onDone
  } = t0;
  const [tab, setTab] = useState("draw");
  const [ink, setInk] = useState(INKS[0].value);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="text-lg">Your signature</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [{
      value: "draw",
      label: "Draw"
    }, {
      value: "type",
      label: "Type"
    }, {
      value: "upload",
      label: "Upload"
    }];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== tab) {
    t3 = <Segmented label="Make a signature by" stretch={true} value={tab} onChange={setTab} options={t2} />;
    $[2] = tab;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== ink || $[5] !== tab) {
    t4 = tab !== "upload" && <div role="group" aria-label="Ink colour" className="flex gap-2">{INKS.map(i => <button key={i.value} type="button" aria-pressed={ink === i.value} onClick={() => setInk(i.value)} className={cn("inline-flex min-h-11 items-center gap-2 rounded-lg border-[1.5px] px-3 text-sm font-medium", ink === i.value ? "border-brand bg-brand-tint" : "border-line-strong bg-card")}><span className="size-4 rounded-full" style={{
          background: i.value
        }} aria-hidden="true" />{i.label}</button>)}</div>;
    $[4] = ink;
    $[5] = tab;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== ink || $[8] !== onDone || $[9] !== tab) {
    t5 = tab === "draw" && <DrawPad ink={ink} onDone={onDone} />;
    $[7] = ink;
    $[8] = onDone;
    $[9] = tab;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== ink || $[12] !== onDone || $[13] !== tab) {
    t6 = tab === "type" && <TypePad ink={ink} onDone={onDone} />;
    $[11] = ink;
    $[12] = onDone;
    $[13] = tab;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onDone || $[16] !== tab) {
    t7 = tab === "upload" && <UploadPad onDone={onDone} />;
    $[15] = onDone;
    $[16] = tab;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="flex flex-col gap-3">{t1}{t3}{t4}{t5}{t6}{t7}</div>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function DrawPad(t0) {
  const $ = _c(26);
  const {
    ink,
    onDone
  } = t0;
  const canvas = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const points = useRef(t1);
  const [empty, setEmpty] = useState(true);
  const [error, setError] = useState("");
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const c = canvas.current;
      const rect = c.getBoundingClientRect();
      c.width = Math.round(rect.width * 2 * (window.devicePixelRatio || 1));
      c.height = Math.round(rect.height * 2 * (window.devicePixelRatio || 1));
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function pos(e) {
      const c_0 = canvas.current;
      const r = c_0.getBoundingClientRect();
      return {
        x: (e.clientX - r.left) / r.width * c_0.width,
        y: (e.clientY - r.top) / r.height * c_0.height
      };
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const pos = t4;
  let t5;
  if ($[4] !== ink) {
    t5 = function stroke() {
      const c_1 = canvas.current;
      const ctx = c_1.getContext("2d");
      const p = points.current;
      if (p.length < 2) {
        return;
      }
      ctx.strokeStyle = ink;
      ctx.lineWidth = c_1.width / 160;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const [a, b, d] = [p[p.length - 3] ?? p[p.length - 2], p[p.length - 2], p[p.length - 1]];
      ctx.beginPath();
      ctx.moveTo((a.x + b.x) / 2, (a.y + b.y) / 2);
      ctx.quadraticCurveTo(b.x, b.y, (b.x + d.x) / 2, (b.y + d.y) / 2);
      ctx.stroke();
    };
    $[4] = ink;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const stroke = t5;
  let t6;
  if ($[6] !== onDone) {
    t6 = async function use() {
      const sig = await trimmedPng(canvas.current);
      if (sig) {
        onDone(sig);
      } else {
        setError("Draw your signature first.");
      }
    };
    $[6] = onDone;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const use = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_0 => {
      e_0.currentTarget.setPointerCapture(e_0.pointerId);
      points.current = [pos(e_0)];
      setError("");
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== empty || $[10] !== stroke) {
    t8 = e_1 => {
      if (points.current.length === 0) {
        return;
      }
      points.current.push(pos(e_1));
      stroke();
      if (empty) {
        setEmpty(false);
      }
    };
    $[9] = empty;
    $[10] = stroke;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      points.current = [];
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8) {
    t10 = <canvas ref={canvas} role="img" aria-label="Signature drawing area. To sign with the keyboard, use Type." data-testid="signature-pad" className="h-40 w-full touch-none rounded-lg border-2 border-dashed border-line-strong bg-white" onPointerDown={t7} onPointerMove={t8} onPointerUp={t9} />;
    $[13] = t8;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <button type="button" className="min-h-11 rounded-lg border-[1.5px] border-ink px-4 font-semibold hover:bg-sand" onClick={() => {
      const c_2 = canvas.current;
      c_2.getContext("2d").clearRect(0, 0, c_2.width, c_2.height);
      setEmpty(true);
    }}>Clear</button>;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(primaryButtonClass, "min-h-11 flex-1 text-base");
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== empty || $[18] !== use) {
    t13 = <div className="flex gap-2">{t11}<button type="button" className={t12} onClick={use} disabled={empty}>Use this signature</button></div>;
    $[17] = empty;
    $[18] = use;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== error) {
    t14 = error && <p role="alert" className="text-sm font-medium text-destructive">{error}</p>;
    $[20] = error;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t10 || $[23] !== t13 || $[24] !== t14) {
    t15 = <div className="flex flex-col gap-2">{t10}{t13}{t14}</div>;
    $[22] = t10;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  return t15;
}
function TypePad(t0) {
  const $ = _c(26);
  const {
    ink,
    onDone
  } = t0;
  const [name, setName] = useState("");
  const [fontId, setFontId] = useState(SIGNATURE_FONTS[0].id);
  let t1;
  if ($[0] !== fontId) {
    t1 = SIGNATURE_FONTS.find(f => f.id === fontId);
    $[0] = fontId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const font = t1;
  let t2;
  if ($[2] !== font.family || $[3] !== ink || $[4] !== name || $[5] !== onDone) {
    t2 = async function use() {
      const css = `${120}px ${font.family}`;
      const primary = font.family.split(",")[0];
      await document.fonts.load(`${120}px ${primary}`, name).catch(_temp);
      const c = document.createElement("canvas");
      const ctx = c.getContext("2d");
      ctx.font = css;
      c.width = Math.ceil(ctx.measureText(name).width + 120);
      c.height = Math.ceil(216);
      ctx.font = css;
      ctx.fillStyle = ink;
      ctx.textBaseline = "middle";
      ctx.fillText(name, 60, c.height / 2);
      const sig = await trimmedPng(c);
      if (sig) {
        onDone(sig);
      }
    };
    $[2] = font.family;
    $[3] = ink;
    $[4] = name;
    $[5] = onDone;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const use = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => setName(e.target.value);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== name) {
    t4 = <label className={labelClass}>Your name<input className={inputClass} value={name} onChange={t3} autoComplete="name" /></label>;
    $[8] = name;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== fontId || $[11] !== ink || $[12] !== name) {
    t5 = SIGNATURE_FONTS.map(f_0 => <button key={f_0.id} type="button" aria-pressed={fontId === f_0.id} aria-label={`${f_0.label} handwriting`} onClick={() => setFontId(f_0.id)} className={cn("flex min-h-14 items-center justify-between gap-2 rounded-lg border-[1.5px] bg-white px-3 text-left", fontId === f_0.id ? "border-brand" : "border-line-strong")}><span className={cn(f_0.className, "truncate text-3xl")} style={{
        color: ink
      }}>{name || "Your name"}</span><span className="shrink-0 text-xs text-muted-foreground">{f_0.label}</span></button>);
    $[10] = fontId;
    $[11] = ink;
    $[12] = name;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <div role="group" aria-label="Handwriting style" className="flex flex-col gap-2">{t5}</div>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn(primaryButtonClass, "min-h-11 text-base");
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== name) {
    t8 = name.trim();
    $[17] = name;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = !t8;
  let t10;
  if ($[19] !== t9 || $[20] !== use) {
    t10 = <button type="button" className={t7} onClick={use} disabled={t9}>Use this signature</button>;
    $[19] = t9;
    $[20] = use;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t4 || $[24] !== t6) {
    t11 = <div className="flex flex-col gap-3">{t4}{t6}{t10}</div>;
    $[22] = t10;
    $[23] = t4;
    $[24] = t6;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp() {}
function UploadPad({
  onDone
}: {
  onDone: (s: Signature) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<ImageBitmap | null>(null);
  const [clearWhite, setClearWhite] = useState(true);
  const [error, setError] = useState("");
  const preview = useRef<HTMLCanvasElement>(null);

  // Redraw the preview whenever the photo or the white-background switch changes.
  useEffect(() => {
    const c = preview.current;
    if (!c || !picked) return;
    const scale = Math.min(1, 1200 / picked.width);
    c.width = Math.round(picked.width * scale);
    c.height = Math.round(picked.height * scale);
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(picked, 0, 0, c.width, c.height);
    if (clearWhite) {
      const img = ctx.getImageData(0, 0, c.width, c.height);
      img.data.set(removeWhiteBackground(img.data));
      ctx.putImageData(img, 0, 0);
    }
  }, [picked, clearWhite]);
  async function load(file: File | undefined) {
    if (!file) return;
    setError("");
    try {
      const kind = kindOf(file);
      const blob = kind === "jpg" || kind === "png" ? file : (await runImageJob(file, {
        format: "png"
      })).blob;
      setPicked(await createImageBitmap(blob, {
        imageOrientation: "from-image"
      }));
    } catch {
      setError("That picture couldn't be opened. Try a JPG or PNG.");
    }
  }
  async function use() {
    const sig = preview.current && (await trimmedPng(preview.current));
    if (sig) onDone(sig);else setError("The picture looks empty. Try turning off “Remove white background”.");
  }
  return <div className="flex flex-col gap-3">
      <button type="button" onClick={() => input.current?.click()} className="min-h-11 rounded-lg border-2 border-dashed border-line-strong px-4 font-semibold hover:bg-sand">
        Choose a photo of your signature
      </button>
      <input ref={input} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="signature-upload" accept={acceptAttribute(IMAGE_KINDS)} onChange={e => {
      load(e.target.files?.[0]);
      e.target.value = "";
    }} />
      {picked && <>
          <canvas ref={preview} role="img" aria-label="Uploaded signature" className="max-h-40 w-full rounded-lg border object-contain [background:repeating-conic-gradient(#eee_0_25%,#fff_0_50%)_0_0/16px_16px]" />
          <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
            <input type="checkbox" className="size-5 accent-brand" checked={clearWhite} onChange={e_0 => setClearWhite(e_0.target.checked)} />
            Remove white background
          </label>
          <button type="button" className={cn(primaryButtonClass, "min-h-11 text-base")} onClick={use}>
            Use this signature
          </button>
        </>}
      {error && <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>}
    </div>;
}